import { lazy, Suspense } from 'react';
import { Navigate, Route } from 'react-router-dom';

import NotFound from 'pages/NotFound';

import Loader from 'shared/Loader';

import { useMyPrograms } from 'hooks/useMyPrograms/useMyPrograms';
import { lazyRetry } from 'utils/lazy';
import { PathName } from './paths';

import { RequireFeatureFlag } from './guards';

const Home = lazy(() => lazyRetry(() => import('pages/Home')));
const OptaviaSupport = lazy(() => lazyRetry(() => import('pages/Support')));
const Messages = lazy(() => lazyRetry(() => import('pages/Messages')));
const Labs = lazy(() => lazyRetry(() => import('pages/Labs')));
const LabResults = lazy(() => lazyRetry(() => import('pages/Labs/components/LabResults')));
const Documents = lazy(() => lazyRetry(() => import('pages/Documents')));
const History = lazy(() => lazyRetry(() => import('pages/History')));
const Settings = lazy(() => lazyRetry(() => import('pages/Settings')));
const About = lazy(() => lazyRetry(() => import('pages/About')));
const Legal = lazy(() => lazyRetry(() => import('pages/Legal')));
const AccountSettings = lazy(() => lazyRetry(() => import('pages/AccountSettings')));
const HealthProfile = lazy(() => lazyRetry(() => import('pages/HealthProfile')));
const MedicalIntakeForm = lazy(() =>
  lazyRetry(() => import('pages/HealthProfile/MedicalIntakeForm'))
);
const HealthMetrics = lazy(() => lazyRetry(() => import('pages/HealthProfile/HealthMetrics')));
const HealthConditions = lazy(() =>
  lazyRetry(() => import('pages/HealthProfile/HealthConditions'))
);
const ChronicDiseases = lazy(() => lazyRetry(() => import('pages/HealthProfile/ChronicDiseases')));
const SocialHistory = lazy(() => lazyRetry(() => import('pages/HealthProfile/SocialHistory')));
const Medications = lazy(() => lazyRetry(() => import('pages/HealthProfile/Medications')));
const Allergies = lazy(() => lazyRetry(() => import('pages/HealthProfile/Allergies')));
const WomensHealth = lazy(() => lazyRetry(() => import('pages/HealthProfile/WomensHealth')));
const MedicalRecords = lazy(() => lazyRetry(() => import('pages/HealthProfile/MedicalRecords')));
const MyPrescriptions = lazy(() => lazyRetry(() => import('pages/Prescriptions/MyPrescriptions')));
const ManageMedication = lazy(() =>
  lazyRetry(() => import('pages/Prescriptions/ManageMedication'))
);
const Pharmacy = lazy(() => lazyRetry(() => import('pages/Prescriptions/Pharmacy')));
const DiscountCard = lazy(() => lazyRetry(() => import('pages/Prescriptions/DiscountCard')));
const AccountInfo = lazy(() => lazyRetry(() => import('pages/AccountSettings/AccountInfo')));
const BillingDetails = lazy(() => lazyRetry(() => import('pages/AccountSettings/BillingDetails')));
const ChangePassword = lazy(() => lazyRetry(() => import('pages/AccountSettings/ChangePassword')));
const SignInAndSecurity = lazy(() =>
  lazyRetry(() => import('pages/AccountSettings/SignInAndSecurity/SignInAndSecurity'))
);
const Insurance = lazy(() => lazyRetry(() => import('pages/AccountSettings/Insurance')));
const NotificationsFeed = lazy(() => lazyRetry(() => import('pages/NotificationsFeed')));
const Notifications = lazy(() => lazyRetry(() => import('pages/AccountSettings/Notifications')));
const MedicaidWaiver = lazy(() => lazyRetry(() => import('pages/Legal/MedicaidWaiver')));
const HipaaAgreement = lazy(() => lazyRetry(() => import('pages/Legal/HipaaAgreement')));
const PaymentMethod = lazy(() => lazyRetry(() => import('pages/PaymentMethod')));
const MyResources = lazy(() => lazyRetry(() => import('pages/MyResources')));
const Article = lazy(() => lazyRetry(() => import('pages/Article')));
const UnpaidInvoice = lazy(() => lazyRetry(() => import('pages/UnpaidInvoice')));
const PartnerBenefit = lazy(() => lazyRetry(() => import('pages/PartnerBenefit')));
const ConfirmOrder = lazy(() => lazyRetry(() => import('pages/ConfirmOrder')));
const PrescriptionTerms = lazy(() => lazyRetry(() => import('pages/PrescriptionTerms')));
const Shop = lazy(() => lazyRetry(() => import('pages/shop/Shop')));
const ShopHome = lazy(() => lazyRetry(() => import('pages/shop/Home')));
const Cart = lazy(() => lazyRetry(() => import('pages/shop/Cart')));
const ShopCheckout = lazy(() => lazyRetry(() => import('pages/shop/Checkout')));
const ShopPrescriptionIntake = lazy(() => lazyRetry(() => import('pages/shop/PrescriptionIntake')));
const Products = lazy(() => lazyRetry(() => import('pages/shop/Products')));
const Product = lazy(() => lazyRetry(() => import('pages/shop/Product')));
const Address = lazy(() => lazyRetry(() => import('pages/shop/ShippingAddress')));
const OrdersHistory = lazy(() => lazyRetry(() => import('pages/shop/OrdersHistory')));
const Order = lazy(() => lazyRetry(() => import('pages/shop/Order')));
const Subscriptions = lazy(() => lazyRetry(() => import('pages/shop/Subscriptions')));

const BillingDetailsGuard = () => {
  const { freemium } = useMyPrograms();

  if (freemium) {
    return <Navigate to={PathName.NotFound} replace />;
  }

  return (
    <Suspense fallback={<Loader isVisible />}>
      <BillingDetails />
    </Suspense>
  );
};

export const getMainLayoutRoutes = () => (
  <>
    <Route element={<Home />} index />
    <Route element={<OptaviaSupport />} path={PathName.Support} />
    <Route element={<Navigate to={PathName.Messages} replace />} path={PathName.AIAssistantChat} />
    <Route
      element={
        <RequireFeatureFlag
          flag="messaging"
          status="paywall"
          fallback={<Navigate to={PathName.Home} replace />}
        >
          {/* Convex auth comes from the single provider in the Main layout. */}
          <Suspense fallback={<Loader isVisible />}>
            <Messages />
          </Suspense>
        </RequireFeatureFlag>
      }
      path={PathName.Messages}
    />
    <Route
      element={
        <RequireFeatureFlag flag="labsAndImaging">
          <Suspense fallback={<Loader isVisible />}>
            <LabResults />
          </Suspense>
        </RequireFeatureFlag>
      }
      path={`${PathName.Labs}/:id`}
    />
    <Route
      element={
        <RequireFeatureFlag flag="labsAndImaging">
          <Labs />
        </RequireFeatureFlag>
      }
      path={PathName.Labs}
    />
    <Route
      element={
        <RequireFeatureFlag flag="documentsAndLinks">
          <Documents />
        </RequireFeatureFlag>
      }
      path={PathName.Documents}
    />
    <Route
      element={
        <RequireFeatureFlag flag="notifications">
          <Suspense fallback={<Loader isVisible />}>
            <NotificationsFeed />
          </Suspense>
        </RequireFeatureFlag>
      }
      path={PathName.NotificationsFeed}
    />
    <Route element={<History />} path={PathName.History} />
    <Route element={<Settings />} path={PathName.Settings} />
    <Route element={<About />} path={PathName.About} />
    <Route element={<Legal />} path={PathName.Legal} />
    <Route element={<AccountSettings />} path={PathName.AccountSettings} />
    <Route element={<RequireFeatureFlag flag="healthProfile" />}>
      <Route element={<HealthProfile />} path={PathName.HealthProfile} />
      <Route element={<MedicalIntakeForm />} path={PathName.MedicalIntakeForm} />
      <Route element={<HealthMetrics />} path={PathName.HealthMetrics} />
      <Route element={<HealthConditions />} path={PathName.HealthConditions} />
      <Route element={<ChronicDiseases />} path={PathName.ChronicDiseases} />
      <Route element={<SocialHistory />} path={PathName.SocialHistory} />
      <Route element={<Medications />} path={PathName.Medications} />
      <Route element={<Allergies />} path={PathName.Allergies} />
      <Route element={<WomensHealth />} path={PathName.WomensHealth} />
      <Route element={<MedicalRecords />} path={PathName.MedicalRecords} />
    </Route>
    <Route element={<MyPrescriptions />} path={PathName.MyPrescriptions} />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <ManageMedication />
        </Suspense>
      }
      path={PathName.ManageMedication}
    />
    <Route element={<Pharmacy />} path={PathName.Pharmacy} />
    <Route element={<DiscountCard />} path={PathName.DiscountCard} />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <AccountInfo />
        </Suspense>
      }
      path={PathName.AccountInformation}
    />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <ChangePassword />
        </Suspense>
      }
      path={PathName.ChangePassword}
    />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <SignInAndSecurity />
        </Suspense>
      }
      path={PathName.SignInAndSecurity}
    />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <Insurance />
        </Suspense>
      }
      path={PathName.Insurance}
    />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <Notifications />
        </Suspense>
      }
      path={PathName.Notifications}
    />
    <Route element={<BillingDetailsGuard />} path={PathName.BillingDetails} />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <MedicaidWaiver />
        </Suspense>
      }
      path={PathName.MedicaidWaiver}
    />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <HipaaAgreement />
        </Suspense>
      }
      path={PathName.HipaaAgreement}
    />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <PaymentMethod />
        </Suspense>
      }
      path={PathName.PaymentMethods + '/:action'}
    />
    <Route element={<MyResources />} path={`${PathName.MyResources}/:programId/:sectionId`} />
    <Route
      element={<Article />}
      path={`${PathName.MyResources}/:programId/:sectionId/:articleId`}
    />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <UnpaidInvoice />
        </Suspense>
      }
      path={PathName.UnpaidInvoice}
    />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <PartnerBenefit />
        </Suspense>
      }
      path={PathName.PartnerBenefit}
    />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <ConfirmOrder />
        </Suspense>
      }
      path={`${PathName.ConfirmOrder}/:invoiceId`}
    />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <PrescriptionTerms />
        </Suspense>
      }
      path={`${PathName.PrescriptionTerms}/:orderId/:lineId`}
    />
    <Route
      element={
        <RequireFeatureFlag flag="store">
          <Suspense fallback={<Loader isVisible />}>
            <Shop />
          </Suspense>
        </RequireFeatureFlag>
      }
      path={PathName.Shop}
    >
      <Route
        element={
          <Suspense fallback={<Loader isVisible />}>
            <ShopHome />
          </Suspense>
        }
        path={PathName.Shop + '/:shopPageSlug?'}
      />
      <Route
        element={
          <Suspense fallback={<Loader isVisible />}>
            <Cart />
          </Suspense>
        }
        path={PathName.ShopCart}
      />
      <Route
        element={
          <Suspense fallback={<Loader isVisible />}>
            <ShopCheckout />
          </Suspense>
        }
        path={PathName.ShopCheckout}
      />
      <Route
        element={
          <Suspense fallback={<Loader isVisible />}>
            <ShopPrescriptionIntake />
          </Suspense>
        }
        path={`${PathName.ShopPrescriptionIntake}/:orderId`}
      />
      <Route
        element={
          <Suspense fallback={<Loader isVisible />}>
            <Products />
          </Suspense>
        }
        path={PathName.ShopProducts}
      />
      <Route
        element={
          <Suspense fallback={<Loader isVisible />}>
            <Address />
          </Suspense>
        }
        path={PathName.ShopAddress}
      />
      <Route
        element={
          <Suspense fallback={<Loader isVisible />}>
            <Product />
          </Suspense>
        }
        path={PathName.ShopProducts + '/:id'}
      />
      <Route
        element={
          <Suspense fallback={<Loader isVisible />}>
            <OrdersHistory />
          </Suspense>
        }
        path={PathName.ShopOrders}
      />
      <Route
        element={
          <Suspense fallback={<Loader isVisible />}>
            <Order />
          </Suspense>
        }
        path={PathName.ShopOrders + '/:id'}
      />
      <Route
        element={
          <RequireFeatureFlag flag="shopRecurring">
            <Suspense fallback={<Loader isVisible />}>
              <Subscriptions />
            </Suspense>
          </RequireFeatureFlag>
        }
        path={PathName.ShopSubscriptions}
      />
      <Route element={<Navigate to={PathName.Shop} />} path="*" />
    </Route>
    <Route element={<NotFound />} path={PathName.NotFound} />
    <Route element={<Navigate to={PathName.NotFound} />} path="*" />
  </>
);
